import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ENDPOINT, IMAGE_BASE_URL } from "../../../endpoints/endpoints";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";

const TipsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [tips, setTips] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);

  // Token check
  useEffect(() => {
    if (!localStorage.getItem("accessToken")) {
      toast.warning("Please log in to continue");
      navigate("/login");
    }
  }, [navigate]);

  // Fetch Tips Detail
  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("accessToken");

        const res = await axios.get(ENDPOINT.TIP_DETAIL(id), {
          headers: { Authorization: `Bearer ${token}` },
        });

        // 🔥 FIXED: your backend uses "result" NOT "data"
        setTips(res.data.result || res.data.data);
      } catch (err) {
        toast.error("Failed to fetch tips detail");
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [id]);

  // Delete handler
  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("accessToken");

      const res = await axios.delete(ENDPOINT.TIP_DELETE(id), {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Tips deleted successfully!");

      setTimeout(() => navigate("/tips/list"), 1200);
    } catch (err) {
      toast.error("Failed to delete tips");
    } finally {
      setDeleteDialog(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <Box display="flex" height="80vh" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  // Not found
  if (!tips) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Typography variant="h6" align="center" color="text.secondary">
          Tips not found.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
      <ToastContainer />

      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={600}>
            🖼 Tips Detail
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/tips/list")}
            sx={{ textTransform: "none" }}
          >
            ← Back to List
          </Button>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        {/* Details */}
        <Stack spacing={2}>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              League
            </Typography>
            <Typography variant="body1"><Box
                                  component="img"
                                  src={`${IMAGE_BASE_URL}${tips.league.image}`} 
                                  alt={tips.league.name}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    mr: 1.5,
                                  }}
                                />{tips.league.name}</Typography>
          </Box>

           <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Home Team
            </Typography>
            <Typography variant="body1"><Box
                                  component="img"
                                  src={`${IMAGE_BASE_URL}${tips.home_team.image}`} 
                                  alt={tips.home_team.name}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    mr: 1.5,
                                  }}
                                />{tips.home_team.name}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Away Team
            </Typography>
            <Typography variant="body1"><Box
                                  component="img"
                                  src={`${IMAGE_BASE_URL}${tips.away_team.image}`} 
                                  alt={tips.away_team.name}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    mr: 1.5,
                                  }}
                                />{tips.away_team.name}</Typography>
          </Box>

           <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Match Time
            </Typography>
            <Typography variant="body1">{dayjs(tips.match_time).format("DD MMM YYYY, HH:mm")}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Tip
            </Typography>
            <Typography variant="body1">{tips.tip}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Analysis
            </Typography>
            <Typography variant="body1">{tips.analysis}</Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Action Buttons */}
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button
            variant="outlined"
            color="warning"
            onClick={() => navigate(`/tips/update/${tips.id}`)}
            sx={{ textTransform: "none" }}
          >
            Edit Tips
          </Button>

          <Button
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
            onClick={() => setDeleteDialog(true)}
          >
            Delete
          </Button>
        </Stack>
      </Paper>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this tips? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TipsDetail;
